import { ClientProfile } from './activity.model';

/** The browser objects the collector reads; passing them in keeps it testable without a browser. */
export interface BrowserEnv {
  win: Window;
  nav: Navigator;
  doc: Document;
}

interface NetworkInformation {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

type NavigatorExtras = Navigator & { deviceMemory?: number; connection?: NetworkInformation };

/** A URL reduced to origin + path: query strings and fragments can carry ids or tokens. */
export function stripQuery(url: string): string {
  try {
    const u = new URL(url, 'http://placeholder.invalid');
    const origin = u.origin === 'http://placeholder.invalid' ? '' : u.origin;
    return `${origin}${u.pathname}`;
  } catch {
    return '';
  }
}

/**
 * About 25 facts the browser can report about itself. They are stored for support and usage
 * analysis and are never trusted: a user can change every one of them. The server adds the
 * facts it can verify (IP, proxy chain, parsed User-Agent) on its own.
 */
export function collectClientProfile(env: BrowserEnv, appVersion: string): ClientProfile {
  const { win, nav, doc } = env;
  const n = nav as NavigatorExtras;
  const media = (q: string): boolean => !!win.matchMedia?.(q).matches;
  const conn = n.connection ?? {};
  return {
    screen_width: win.screen?.width ?? 0,
    screen_height: win.screen?.height ?? 0,
    viewport_width: win.innerWidth ?? 0,
    viewport_height: win.innerHeight ?? 0,
    pixel_ratio: win.devicePixelRatio ?? 1,
    color_depth: win.screen?.colorDepth ?? 0,
    orientation: win.screen?.orientation?.type ?? '',
    prefers_dark_mode: media('(prefers-color-scheme: dark)'),
    display_mode: media('(display-mode: standalone)') ? 'standalone' : 'browser',
    language: n.language ?? '',
    languages: (n.languages ?? []).join(','),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
    // getTimezoneOffset is minutes WEST of UTC; report minutes EAST (IST = 330).
    timezone_offset_min: -new Date().getTimezoneOffset(),
    platform: n.platform ?? '',
    cpu_cores: n.hardwareConcurrency ?? 0,
    device_memory_gb: n.deviceMemory ?? 0,
    touch_points: n.maxTouchPoints ?? 0,
    online: n.onLine ?? true,
    connection_type: conn.type ?? '',
    effective_type: conn.effectiveType ?? '',
    downlink_mbps: conn.downlink ?? 0,
    rtt_ms: conn.rtt ?? 0,
    cookies_enabled: n.cookieEnabled ?? false,
    do_not_track: n.doNotTrack === '1',
    app_version: appVersion,
    entry_route: win.location?.pathname ?? '',
    referrer: doc.referrer ? stripQuery(doc.referrer) : ''
  };
}
